import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSiteColors, SiteText as Text } from '@/components/Sites/siteTheme';
import { sitesApi } from '@/lib/api/sites';
import { effectiveSiteStatus, SITES_DOMAIN } from '@/lib/publishedSites';
import { requestSitePublication, siteNameError, suggestedSiteName } from '@/lib/workSitePublishing';
import Icon from '@/components/Icon';
import { PublishedSiteCard } from './WorkPublishedSites';


function Detail({ icon, title, children, colors }) {
  return <View style={styles.detail}>
    <View style={styles.detailIcon}><Icon name={icon} size={15} color={colors.text_color_secondary} /></View>
    <View style={styles.copy}><Text size="s" weight="semibold">{title}</Text><Text size="s" color="secondary" style={styles.bodyCopy}>{children}</Text></View>
  </View>;
}

// Keep the form inside the artifact: it works in chat, narrow viewers and on
// native without moving focus into a second modal above a playing game.
export default function WorkSitePublish({ taskId, file, artifact, prepareFile }) {
  const key = `${taskId || ''}:${file?.id || file?._id || artifact?.path || file?.parameters?.workArtifactPath || ''}`;
  if (file?.canPublishSite === false) return null;
  if (!taskId && !file?.id && !file?._id && !prepareFile) return null;
  return <PublishCreation key={key} taskId={taskId} file={file} artifact={artifact} prepareFile={prepareFile} />;
}

function PublishCreation({ taskId, file, artifact, prepareFile }) {
  const colors = useSiteColors();
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState(() => suggestedSiteName(file?.name || artifact?.name || artifact?.path));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [published, setPublished] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [checkVersion, setCheckVersion] = useState(0);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const lock = useRef(false);
  const input = useRef(null);
  const opener = useRef(null);
  const restoreFocus = useRef(false);
  const validation = siteNameError(name);
  const live = published && effectiveSiteStatus(published) === 'live';
  const checked = availability?.name === name ? availability : null;
  const localOnly = (checked?.domain || SITES_DOMAIN).endsWith('.localhost');
  const checking = !validation && (!checked || checked.loading);
  const owned = !!checked?.result?.owned;
  const notEntitled = !!plan && !plan.error && !plan.limits;
  const full = !!plan?.limits && plan.live >= plan.limits.maxLive && !plan.liveNames.has(name);
  const blocked = !!validation || !checked?.result?.available || checking || full || notEntitled || loadingPlan || !!plan?.error || (owned && !replaceExisting);

  useEffect(() => {
    if (expanded && !published) input.current?.focus?.();
    if (!expanded && restoreFocus.current) { opener.current?.focus?.(); restoreFocus.current = false; }
  }, [expanded, published]);

  useEffect(() => {
    if (!expanded) return undefined;
    let current = true;
    setLoadingPlan(true);
    sitesApi.list().then(out => {
      if (!current) return;
      const liveSites = (out?.sites || []).filter(site => effectiveSiteStatus(site) === 'live');
      setPlan({ limits: out?.limits || null, live: liveSites.length, liveNames: new Set(liveSites.map(site => site.name)) });
    }).catch(() => { if (current) setPlan({ error: 'Could not check your plan. Close and reopen to retry.' }); }).finally(() => { if (current) setLoadingPlan(false); });
    return () => { current = false; };
  }, [expanded]);

  useEffect(() => {
    if (!expanded || published || validation) return undefined;
    let current = true;
    setAvailability({ name, loading: true });
    setReplaceExisting(false);
    const timer = setTimeout(() => {
      sitesApi.names(name).then(out => {
        if (!current) return;
        setAvailability({ name, result: out?.results?.find(result => result.name === name), suggestions: out?.suggestions || [], domain: out?.domain });
      }).catch(err => {
        if (current) setAvailability({ name, error: err?.message || 'Could not check availability.' });
      });
    }, 300);
    return () => { current = false; clearTimeout(timer); };
  }, [expanded, published, name, validation, checkVersion]);

  const changeName = value => { setName(value.toLowerCase().trim()); setError(''); setReplaceExisting(false); };
  const open = () => { setExpanded(true); setError(''); };
  const close = () => { restoreFocus.current = true; setExpanded(false); };
  const publish = async () => {
    if (lock.current || blocked) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const out = await requestSitePublication({ taskId, name, file, artifact, ...(prepareFile ? { prepareFile } : {}), replaceExisting });
      setPublished(out.site);
    } catch (err) {
      setError(err?.message || 'Could not start publishing. Please try again.');
      setCheckVersion(value => value + 1);
    } finally { lock.current = false; setBusy(false); }
  };

  const button = (label, onPress, { primary = false, disabled = false, icon, ref } = {}) => <Pressable
    ref={ref} accessibilityRole="button" accessibilityLabel={label} accessibilityState={{ disabled, busy: busy && primary }}
    disabled={disabled} onPress={onPress}
    style={({ hovered, pressed }) => [styles.button, { backgroundColor: primary ? colors.text_color_primary : colors.solid_color_secondary }, (hovered || pressed || disabled) && { opacity: disabled ? 0.45 : 0.8 }]}>
    {busy && primary ? <ActivityIndicator size="small" color={colors.solid_color_primary} /> : icon ? <Icon name={icon} size={13} color={primary ? colors.solid_color_primary : colors.text_color_primary} /> : null}
    <Text size="s" weight="semibold" style={{ color: primary ? colors.solid_color_primary : colors.text_color_primary }}>{label}</Text>
  </Pressable>;

  return <View style={[styles.container, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}>
    <View style={styles.heading}>
      <View style={[styles.globe, { backgroundColor: colors.solid_color_secondary }]}><Icon name={live ? 'check' : 'globe'} size={19} color={colors.text_color_primary} /></View>
      <View style={styles.copy}>
        <Text size={expanded ? 'l' : 's'} weight="semibold">{live ? localOnly ? 'Local site ready' : 'Ready to share' : busy ? 'Publishing your site' : localOnly ? 'Test your site locally' : 'Give your creation a public link'}</Text>
        <Text size="s" color="secondary" style={styles.bodyCopy}>{localOnly ? 'Only available on this Mac. Production sites are unaffected.' : live ? 'Send the link to friends. They can open it right away.' : busy ? 'Your link will appear here after publishing succeeds.' : 'Let friends play, explore, and enjoy what you made.'}</Text>
      </View>
      {!expanded && button(live ? 'Share link' : 'Publish site', open, { primary: true, icon: 'arrow-up-right-from-square', ref: opener })}
    </View>
    {expanded && <View style={styles.form}>
      {live ? <PublishedSiteCard site={published} onUnpublished={() => { setPublished(null); setCheckVersion(value => value + 1); }} /> : <>
        <View style={styles.nameSection}>
          <Text size="s" weight="semibold">Site address</Text>
          <View style={[styles.inputWrap, { borderColor: nameFocused ? colors.text_color_primary : colors.text_color_secondary, backgroundColor: colors.solid_color_primary }, nameFocused && Platform.OS === 'web' && { boxShadow: `0 0 0 1px ${colors.text_color_primary}` }]}>
            <Icon name="globe" size={14} color={colors.text_color_secondary} />
            <TextInput ref={input} accessibilityLabel="Site name" value={name} onChangeText={changeName}
              onFocus={() => setNameFocused(true)} onBlur={() => setNameFocused(false)}
              autoCapitalize="none" autoCorrect={false} maxLength={40} editable={!busy} selectTextOnFocus
              onSubmitEditing={publish} style={[styles.input, { color: colors.text_color_primary }]} />
          </View>
          <Text size="xs" color="secondary" selectable style={styles.address}>{name || 'your-name'}.{checked?.domain || SITES_DOMAIN}</Text>
          <Text size="xs" color="secondary" accessibilityLiveRegion="polite">{validation || (checking ? 'Checking availability…' : checked?.error || checked?.result?.message || (checked?.result?.available ? 'This name is available.' : 'Choose another name.'))}</Text>
          {checked?.error && button('Retry name check', () => setCheckVersion(value => value + 1))}
          {!checking && checked?.result && !checked.result.available && <View style={styles.suggestions}>
            {(checked.suggestions || []).filter(value => value !== name).slice(0, 3).map(value => <Pressable key={value} accessibilityRole="button" accessibilityLabel={`Use ${value}`} onPress={() => changeName(value)} style={[styles.suggestion, { backgroundColor: colors.solid_color_secondary }]}><Text size="xs">{value}</Text></Pressable>)}
          </View>}
          {owned && <Pressable accessibilityRole="checkbox" accessibilityLabel="Replace existing site" accessibilityState={{ checked: replaceExisting, disabled: busy }} disabled={busy} onPress={() => setReplaceExisting(value => !value)} style={styles.replacement}>
            <Icon name={replaceExisting ? 'square-check' : 'square'} size={18} color={colors.text_color_primary} />
            <Text size="s" style={styles.copy}>Replace my existing site at this address.</Text>
          </Pressable>}
        </View>
        <View style={styles.details}>
          <Detail icon="link" title={localOnly ? 'A local test link' : 'One link. Anyone can open it.'} colors={colors}>{localOnly ? 'Open it in a browser on this Mac. This address cannot be shared publicly.' : 'Friends can use it in their browser. No Gab AI account needed.'}</Detail>
          <Detail icon="file-code" title="Share the creation, keep the chat private" colors={colors}>Only this page and the files it needs will be published.</Detail>
          <Detail icon="clock" title="You stay in control" colors={colors}>{plan?.limits ? `Your plan keeps sites online for ${plan.limits.ttlDays} days (${plan.live} of ${plan.limits.maxLive} live sites used).` : 'Sites stay online for a limited time based on your plan.'} Manage links or unpublish anytime in My sites.</Detail>
        </View>
        {notEntitled && <Text size="s" accessibilityRole="alert">Publishing sites requires Gab AI Plus.</Text>}
        {full && <Text size="s" accessibilityRole="alert">Your live site limit is reached. Unpublish a site in My sites to make room.</Text>}
        {!!plan?.error && <Text size="s" accessibilityRole="alert">{plan.error}</Text>}
        {!!error && <Text size="s" accessibilityRole="alert">{error}</Text>}
        <View style={[styles.footer, { borderTopColor: colors.border_color_secondary }]}>
          <Text size="xs" color="secondary" style={styles.footerCopy}>{localOnly ? 'Publishes a local test copy.' : 'Publishing makes these files public.'}{ '\n' }No extra Work credits needed.</Text>
          {button(busy ? 'Publishing…' : loadingPlan ? 'Checking plan…' : checking ? 'Checking name…' : owned ? 'Replace site' : 'Publish site', publish, { primary: true, disabled: blocked || busy, icon: 'arrow-up-right-from-square' })}
        </View>
      </>}
      <View style={styles.bottomRow}>
        <Pressable accessibilityRole="button" accessibilityLabel="My sites" onPress={() => router.push('/sites')} style={styles.textButton}><Icon name="globe" size={12} color={colors.text_color_secondary} /><Text size="xs" color="secondary" style={styles.underlined}>My sites</Text></Pressable>
        {!busy && <Pressable accessibilityRole="button" accessibilityLabel={live ? 'Done' : 'Cancel publishing'} onPress={close} style={styles.textButton}><Text size="xs" color="secondary">{live ? 'Done' : 'Cancel'}</Text></Pressable>}
      </View>
    </View>}
  </View>;
}

const styles = StyleSheet.create({
  container: { borderWidth: 1, borderRadius: 16, padding: 16, gap: 20, width: '100%' },
  heading: { flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  globe: { width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center' },
  copy: { flex: 1, minWidth: 150, gap: 3 },
  bodyCopy: { lineHeight: 20 },
  button: { minHeight: 44, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 11, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  form: { gap: 20 },
  nameSection: { gap: 8 },
  inputWrap: { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 10, paddingHorizontal: 12 },
  input: { flex: 1, minWidth: 0, height: 46, fontSize: 16, ...Platform.select({ web: { outlineStyle: 'none' } }) },
  address: { ...Platform.select({ web: { overflowWrap: 'anywhere' } }) },
  details: { gap: 20 },
  detail: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  detailIcon: { width: 20, alignItems: 'center', paddingTop: 3 },
  footer: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 14, borderTopWidth: 1, paddingTop: 16 },
  footerCopy: { flex: 1, minWidth: 175, lineHeight: 18 },
  bottomRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: -8 },
  textButton: { minHeight: 36, flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 2 },
  underlined: { textDecorationLine: 'underline' },
  suggestions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  suggestion: { minHeight: 36, justifyContent: 'center', borderRadius: 8, paddingHorizontal: 10 },
  replacement: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 44 },
  notice: { padding: 16, borderRadius: 12, flexDirection: 'row', alignItems: 'center', gap: 12 },
});
